import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Card,
  IconFileDownload,
  MenuListItem,
  Checkbox,
  TextButton,
  IconCheckCircle,
  Toast,
  TOAST_VARIANTS,
  TOAST_POSITIONS,
  TOAST_VISIBILITY_DURATIONS,
  Loader,
} from 'cdk-radial';
import { screens } from '../../../../config/uiFactory.config';
import { setScreen } from '../../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { useState, useEffect } from 'react';
import OrderAPIStepperFooter from '../StepperFooter/OrderAPIStepperFooter';
import './ReviewTerms.scss';
import styled from 'styled-components';
import {
  createOrder,
  resetErrorUpdatingOrder,
  updateOrder,
  updateTermsAndConditionsMapping,
  saveBundleConsents,
} from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import {
  AMPLITUDE_EVENTS,
  AMPLITUDE_PROPERTIES,
} from '../../../../../constants/amplitudeConstants';
import { getOrgAmplitudeProperties, sendAmplitudeData } from '../../../../../utils/amplitude';
import { ORDER_APIS_STEPPER_FOOTER } from '../../../../../constants/APIDirectoryConstants';
import { API_TYPE, Constants, RESOURCE_TYPES } from '../../../../../constants/constants';
import { resetErrorCreatingOrder } from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import { setfromPurchaseFlowFlag } from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import { trackTermsAccepted } from '../../../../../utils/gtm';

const StyledIconCheckCircle = styled(IconCheckCircle)`
  fill: #196fe1;
  width: 16px;
  height: 16px;
`;
const parseDocKeyFromUrl = (url) => {
  const urlParts = url?.split('/');
  const parsedKey =
    urlParts?.length > 5
      ? `${[urlParts[3], urlParts[4]].join('/')}/${urlParts[5].split('?')[0]}`
      : '';
  return parsedKey;
};

const createPdfObjectUrlFromBinary = (binaryOrBase64) => {
  if (!binaryOrBase64) {
    return '';
  }

  let binary = binaryOrBase64;
  try {
    binary = atob(binaryOrBase64);
  } catch (error) {
    binary = binaryOrBase64;
  }

  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  const blob = new Blob([bytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
};

const getBundleStampedDocument = (bundle, stampedTermsDocuments = []) => {
  const bundleName = bundle?.details?.name?.trim();
  if (!bundleName) {
    return undefined;
  }

  return stampedTermsDocuments.find(
    (doc) => doc?.bundleName?.trim().toLowerCase() === bundleName.toLowerCase(),
  );
};

const getBundleConsentLinkMap = (bundleConsentLinks = []) => {
  return (bundleConsentLinks || []).reduce((acc, entry) => {
    const bundleId = entry?.bundleId;
    if (bundleId && entry?.consentLink) {
      acc[bundleId] = entry.consentLink;
    }
    return acc;
  }, {});
};


const getIntegrationPlanID = (integrations=[])=>{
  let planID = integrations[0]?.pricingPlanId;
  integrations.map((integration)=>{
    if(integration?.status ==='pending'){
      planID = integration?.pricingPlanId
    }
  })
  return planID
}
export const createConsentPayload = (termUrl, item, bundleConsentLinkMap) => {
  const { resourceType, resourceId, resourcePricingPlanId } = item;
  const isBundle = resourceType === RESOURCE_TYPES.BUNDLE;
  const isAsyncApi = resourceType === API_TYPE.ASYNC;
  
  // Common properties for all resource types
  const basePayload = {
    signedTermsAndConditions: termUrl,
    signedTermsAndConditionsS3: termUrl,
    documentLink: termUrl,
    acceptedPricePlanId: resourcePricingPlanId,
  };

  let consentPayload;

  if (isBundle) {
    consentPayload = {
      bundleId: resourceId,
      apiProviderId: undefined,
      asyncApiTermsDocKey: undefined,
      documentLink: bundleConsentLinkMap[resourceId],
    };
  } else {
    const apiId = isAsyncApi ? resourceId : `api-v2-${resourceId}`;
    consentPayload = {
      api: apiId,
      apiProviderId: apiId,
      asyncApiTermsDocKey: isAsyncApi ? parseDocKeyFromUrl(termUrl) : undefined,
      ...basePayload,
    };
  }

  return {
    file: {
      termsDocument: isBundle
        ? (bundleConsentLinkMap?.[resourceId] ?? termUrl)
        : termUrl,
    },
    consentPayload,
  };
};

export const createPayload = (cart, orders, bundleConsentLinkMap) => {
  const payload = {
    apiProducts: [],
    asyncApiIntegrations: [],
    apiPlanIds: [],
    consentData: {},
    bundleIntegrations: [],
  };

  cart.forEach((item) => {
    if (item.resourceType === 'api') {
      payload.apiProducts.push('api-v2-' + item.resourceId);
    } else if (item.resourceType === API_TYPE.ASYNC) {
      payload.asyncApiIntegrations.push({
        asyncApi: {
          id: item.resourceId,
        }
      });
    } else if (item.resourceType === RESOURCE_TYPES.BUNDLE) {
      payload.bundleIntegrations.push({
        bundleId: item.resourceId,
        pricePlanId: item.resourcePricingPlanId,
        selectedCMFNumber: item.selectedCMFNumber,
        selectedStoreId: item.selectedStoreId
      });
    }

    if (item.resourceType !== RESOURCE_TYPES.BUNDLE) {
      payload.apiPlanIds.push({
        apiId:
          item.resourceType === API_TYPE.ASYNC
            ? item.resourceId
            : 'api-v2-' + item.resourceId,
        planId: item.resourcePricingPlanId,
      });
    }
    const termsDocument =
      item?.details?.terms?.find((t) => t.type === 'terms') || {};

    payload.consentData[item.resourceId] = createConsentPayload(termsDocument?.url, item, bundleConsentLinkMap);
  });

  orders?.restApis?.forEach((item) => {
    payload.apiProducts.push('api-v2-' + item?.id);
    payload.apiPlanIds.push({
      apiId: 'api-v2-' + item?.id,
      planId: getIntegrationPlanID(item?.integrations),
    });
  });

  orders?.asyncApis?.forEach((item) => {
    payload?.asyncApiIntegrations?.push({
      asyncApi: {
        id: item?.id,
      },
      webhook: {
        url: item?.integrations[0]?.webhookUrl,
      },
    });
    payload?.apiPlanIds?.push({
      apiId: item?.id,
      planId:  getIntegrationPlanID(item?.integrations),
    });
  });

  orders?.bundleIntegrations?.forEach((item) => {
    payload.bundleIntegrations.push({
      bundleId: item?.id,
      pricePlanId: item?.integrations[0]?.pricingPlanId, // it will have only one integration
    });
  });

  payload.apiPlanIds  = payload.apiPlanIds.filter(f => f.planId !== "null");

  return payload;
};

export default function ReviewTerms({ setActiveStep, activeStep, cart }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const state = useSelector((state) => state);
  const {
    termsAndConditionsSelected,
    errorCreatingOrder,
    orderCreated,
    orders,
    errorUpdatingOrder,
    orderUpdated,
    stampedTermsDocuments,
    fetchingStampedTermsDocuments,
  } = state?.orderAPISlice;
  const [showToast, setShowToast] = useState(false);
  const [selectedResource, setSelectedResource] = useState('');
  const [selectedAPITerms, setSelectedAPITerms] = useState('');
  const { authenticationToken: token, fortellisEnterpriseEntityId } =
    state?.loginDetailsSlice;
  const [apiResources, setApiResources] = useState([]);
  const [bundleResources, setBundleResources] = useState([]);
  
  const fetchingOrderDetails = state?.orderAPISlice?.fetchingOrderDetails;
   const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice
  );
  useEffect(() => {
    const apiResourcesList = [];
    const bundleResourcesList = [];
    cart.forEach((resource) => {
      if (resource.resourceType === RESOURCE_TYPES.BUNDLE) {
        bundleResourcesList.push(resource);
      } else {
        apiResourcesList.push(resource);
      }
    });

    setApiResources(apiResourcesList);
    setBundleResources(bundleResourcesList);
    if(bundleResourcesList.length > 0){
      setSelectedResource(bundleResourcesList[0]?.resourceId);
    } else {
      setSelectedResource(apiResourcesList[0]?.resourceId);
    }
  }, [cart]);

  useEffect(() => {
    if (!selectedResource) {
      setSelectedAPITerms('');
      return;
    }

    const selectedBundle = bundleResources.find(
      (bundle) => bundle?.resourceId === selectedResource
    );

    if (selectedBundle) {
      const stampedBundleDoc = getBundleStampedDocument(
        selectedBundle,
        stampedTermsDocuments
      );
      if (stampedBundleDoc?.document) {
        const objectUrl = createPdfObjectUrlFromBinary(stampedBundleDoc.document);
        setSelectedAPITerms(objectUrl);
        return () => {
          URL.revokeObjectURL(objectUrl);
        };
      }
      setSelectedAPITerms('');
      return undefined;
    }

    const selectedApi = apiResources.find(
      (api) => api?.resourceId === selectedResource
    );
    
    const termsDocument = selectedApi?.details?.terms?.find((t) => t.type === 'terms') || {};
    setSelectedAPITerms(termsDocument?.url || '');
    return undefined;
  }, [selectedResource, bundleResources, apiResources, stampedTermsDocuments]);
  useEffect(() => {
    setShowToast(errorCreatingOrder || errorUpdatingOrder);
  }, [errorCreatingOrder, errorUpdatingOrder]);

  const handleResourceSelection = (resource) => {
    setSelectedResource(resource?.resourceId);
  };

  const handleSelectAllCheckBox = () => {
    dispatch(updateTermsAndConditionsMapping());
    if (!termsAndConditionsSelected) {
      trackTermsAccepted();
    }
    if (!termsAndConditionsSelected) {
      sendAmplitudeData(AMPLITUDE_EVENTS.ACCEPTED_ALL_TERMS, {
        [AMPLITUDE_PROPERTIES.APIS]: cart.map((api) => {
          return {
            [AMPLITUDE_PROPERTIES.API_NAME]: api?.details?.name || '',
            [AMPLITUDE_PROPERTIES.API_ID]: api?.apiId || '',
            [AMPLITUDE_PROPERTIES.API_PUBLISHER_NAME]:
              api?.details?.orgName || '',
          };
        }),
      });
    }
  };

  const handleDownloadAll = () => {
    cart.forEach((resource) => {
      if (resource.resourceType === RESOURCE_TYPES.BUNDLE) {
        const stampedBundleDoc = getBundleStampedDocument(resource, stampedTermsDocuments);
        if (stampedBundleDoc?.document) {
          const objectUrl = createPdfObjectUrlFromBinary(stampedBundleDoc.document);
          const fileName = `${resource?.details?.name || 'bundle'}-terms.pdf`;
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
          }, 0);
        }
      } else {
        const termsDoc = resource?.details?.terms?.find((t) => t.type === 'terms') || {};
        const url = termsDoc?.url;
        const fileName = termsDoc?.fileName;
        if (!url) return;
        fetch(url)
          .then((response) => response.blob())
          .then((blob) => {
            const a = document.createElement('a');
            const objectUrl = URL.createObjectURL(blob);
            a.href = objectUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              URL.revokeObjectURL(objectUrl);
            }, 0);
          })
          .catch((err) => console.error('Error downloading PDF:', err));
      }
    });
    sendAmplitudeData(AMPLITUDE_EVENTS.TERMS_DOWNLOAD, orgAmplitudeProperties);
  };
  const handleCompletePurchase = async () => {
    try {
      // Save bundle consents if bundles are in cart
      const bundleConsentsData = {};
      cart.forEach((item) => {
        if (item.resourceType === RESOURCE_TYPES.BUNDLE) {
          const stampedBundleDoc = getBundleStampedDocument(
            item,
            stampedTermsDocuments,
          );
          if (stampedBundleDoc?.document) {
            bundleConsentsData[item.resourceId] = {
              consentDocument: stampedBundleDoc.document,
              bundleName: item.details.name,
              documentLink: stampedBundleDoc.documentLink,
            };
          }
        }
      });

      // Save bundle consents in batches of 5 if there are any bundles.
      const allBundleEntries = Object.entries(bundleConsentsData).map(
        ([bundleId, data]) => ({
          bundleId,
          bundleName: data.bundleName,
          pdfDocument: data.consentDocument,
        }),
      );
      const BATCH_SIZE = 5;
      const saveBundleConsentPromises = [];
      for (let i = 0; i < allBundleEntries.length; i += BATCH_SIZE) {
        const batch = allBundleEntries.slice(i, i + BATCH_SIZE);
        saveBundleConsentPromises.push(
          dispatch(
            saveBundleConsents({
              orgId: fortellisEnterpriseEntityId,
              bundles: batch,
              token,
            }),
          ).unwrap(),
        );
      }
      if (saveBundleConsentPromises.length) {
        await Promise.all(saveBundleConsentPromises);
      }
      const latestBundleConsentLinks = dispatch((_, getState) => {
        return getState()?.orderAPISlice?.bundleConsentLinks || [];
      });

      const bundleConsentLinkMap = getBundleConsentLinkMap(
        latestBundleConsentLinks,
      );
      const payload = createPayload(cart, orders, bundleConsentLinkMap);

      if (orders?.app?.status) {
        await dispatch(
          updateOrder({ payload, token, appId: orders?.app?.id }),
        ).unwrap();
        dispatch(setfromPurchaseFlowFlag());
        sendAmplitudeData(AMPLITUDE_EVENTS.UPDATE_PURCHASE, {});
      } else {
        await dispatch(createOrder({ payload, token })).unwrap();
        dispatch(setfromPurchaseFlowFlag());
        sendAmplitudeData(AMPLITUDE_EVENTS.COMPLETE_PURCHASE, {});
      }
      if (orderCreated || orderUpdated) {
        history.push('/dp/my-apis');
        dispatch(setScreen({ screen: screens.MY_APIS }));
      }
    } catch (error) {
      console.error('Error updating or creating order:', error);
    }
  };

  return (
    <>
      {fetchingStampedTermsDocuments ? (
     <div className="loader">
              <Loader label="fetching documents..." />
            </div>
      ) : (
        <>
          <Card className="review-container-card">
        <div className="terms-title-div">
          <span className="terms-container-header">
            {Constants.TERMS_AND_CONDITIONS}
          </span>
          <div className="desc-and-download-conatiner">
            <span className="terms-title-desc">
              {cart.length > 1
                ? Constants.TERMS_AND_CONDITIONS_TEXT
                : Constants.TERMS_AND_CONDITIONS_TEXT_FOR_SINGLE_RESOURCE}
            </span>
            <span>
              <TextButton
                icon={<IconFileDownload />}
                text="Download All"
                onClick={handleDownloadAll}
              />
            </span>
          </div>
        </div>
        <div className="terms-section-div">
          <div className="terms-card">
            <div className="review-terms-api-section-div">
              <div className="api-section-title">Name</div>
              <div className="api-section">
                <div className="review-terms-api-name-item">
                  {!!bundleResources.length && (
                    <>
                      {bundleResources.map((bundle, index) => (
                        <MenuListItem
                          onClick={() => handleResourceSelection(bundle)}
                          dataTestId="menu-list-item"
                          hideSelectedItemCheckmark={true}
                          className={
                            selectedResource === bundle?.resourceId
                              ? 'review-terms-selected-api-name-item'
                              : 'review-terms-unselected-api-name-item'
                          }
                          key={index}
                        >
                          <span className="api-menu-list-wrapper">
                            <div className="select-api-item-card-api-row">
                              {termsAndConditionsSelected ? (
                                <span className="selected-check-icon">
                                  <StyledIconCheckCircle />
                                </span>
                              ) : (
                                <span className="plan-unselected"></span>
                              )}
                              <div
                                className={
                                  selectedResource === bundle?.resourceId
                                    ? 'selected-bundle-item-bundle-name'
                                    : 'select-bundle-item-bundle-name'
                                }
                              >
                                <span className='bundle-item-name'>
                                  {bundle?.details?.name}
                                </span>
                                <span>
                                   {Constants.BUNDLE_SUFFIX}
                                </span>
                              </div>
                            </div>
                          </span>
                        </MenuListItem>
                      ))}
                      <div className='review-terms-item-divider'></div>
                    </>
                  )}
                  {!!apiResources.length &&
                    apiResources.map((api, index) => (
                      <MenuListItem
                        onClick={() => handleResourceSelection(api)}
                        dataTestId="menu-list-item"
                        hideSelectedItemCheckmark={true}
                        className={
                          selectedResource === api?.resourceId
                            ? 'review-terms-selected-api-name-item'
                            : 'review-terms-unselected-api-name-item'
                        }
                        key={index + api.resourceType}
                      >
                        <span className="api-menu-list-wrapper">
                          <div className="select-api-item-card-api-row">
                            {termsAndConditionsSelected ? (
                              <span className="selected-check-icon">
                                <StyledIconCheckCircle />
                              </span>
                            ) : (
                              <span className="plan-unselected"></span>
                            )}
                            <div
                              className={
                                selectedResource === api?.resourceId
                                  ? 'selected-api-item-api-name'
                                  : 'select-api-item-api-name'
                              }
                            >
                              {api?.details?.name}
                            </div>
                          </div>
                        </span>
                      </MenuListItem>
                    ))}
                </div>
              </div>
            </div>
            <div>
              <embed
                type="application/pdf"
                src={selectedAPITerms}
                className="terms-document"
                data-testid="pdf-embed"
              />
            </div>
          </div>
        </div>
        <div className="accept-terms-checkbox">
          <Checkbox
            dataTestId="agree-terms-checkbox"
            id="agree-terms-checkbox"
            label={Constants.AGREE_TERMS_CONDITIONS}
            labelPosition="after"
            name="agree-terms-checkbox"
            onChange={handleSelectAllCheckBox}
            size="standard"
            checked={termsAndConditionsSelected}
          />
        </div>
        <div className="order-api-stepper-footer">
          <OrderAPIStepperFooter
            setActiveStep={setActiveStep}
            activeStep={activeStep}
            isContinueDisabled={!termsAndConditionsSelected}
            step={ORDER_APIS_STEPPER_FOOTER.REVIEW_TERMS}
            handleCompletePurchase={handleCompletePurchase}
            cart={cart}
          />
        </div>
      </Card>
      {showToast && (
        <Toast
          variant={TOAST_VARIANTS.NEGATIVE}
          content={Constants.API_PURCHASE_FAILED_MESSAGE}
          id={'contact-provider-email-toast'}
          actions={[
            {
              onClick: () => {
                setShowToast(false);
                dispatch(resetErrorCreatingOrder());
                dispatch(resetErrorUpdatingOrder());
              },
              text: 'Dismiss',
            },
          ]}
          onVisibilityDurationEnd={() => {
            setShowToast(false);
            dispatch(resetErrorCreatingOrder());
            dispatch(resetErrorUpdatingOrder());
          }}
          position={TOAST_POSITIONS.FIXED}
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
          style={{ marginTop: '64px' }}
        />
      )}
        </>
      )}
    </>
  );
}